<script setup>
import { useRouter } from 'vue-router';
import ANButton from '@/components/common/AnButton.vue';

const router = useRouter();

// Navigate to calculators page
const navigateToCalculators = () => {
  router.push('/financial-calculators');
};
</script>

<template>
  <section id="financial-tools" class="financial-calculators-section">
    <div class="section-container">
      <!-- Concise Section Content -->
      <div class="calculators-content">
        <div class="calculators-info">
          <h2 class="section-title">Financial Planning Tools</h2>
          <p class="section-description">
            Complement your investment strategy with our suite of financial calculators.
            Make data-driven decisions for SIP planning, retirement withdrawals, and property investments.
          </p>

          <!-- Quick Tools List -->
          <div class="tools-list">
            <div class="tool-item">
              <span class="tool-icon">💰</span>
              <span class="tool-name">SIP & Step-up SIP</span>
            </div>
            <div class="tool-item">
              <span class="tool-icon">💸</span>
              <span class="tool-name">SWP & Future Value</span>
            </div>
            <div class="tool-item">
              <span class="tool-icon">🏠</span>
              <span class="tool-name">Rent vs Buy Analysis</span>
            </div>
            <div class="tool-item future-tool">
              <span class="tool-icon">🎯</span>
              <span class="tool-name">Goal-Based Planning*</span>
            </div>
            <div class="tool-item future-tool">
              <span class="tool-icon">🔄</span>
              <span class="tool-name">Scenario Analysis*</span>
            </div>
          </div>

          <p class="future-note">*Coming Soon - Advanced planning features</p>
        </div>

        <div class="calculators-action">
          <div class="action-content">
            <h3>Ready to Calculate?</h3>
            <p>Free tools with city-specific data and interactive visualizations</p>
            <ANButton
              label="Explore All Calculators"
              severity="primary"
              size="large"
              @click="navigateToCalculators"
              class="explore-button"
            />
            <div class="action-features">
              <span>✓ No signup required</span>
              <span>✓ Instant results</span>
              <span>✓ Indian market data</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.financial-calculators-section {
  padding: 60px 0;
  background: linear-gradient(135deg, rgba(0, 78, 80, 0.02) 0%, rgba(0, 78, 80, 0.05) 100%);
  font-family: "Satoshi-Variable", sans-serif;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.calculators-content {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 60px;
  align-items: center;
}

.section-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 16px;
  background: linear-gradient(135deg, var(--an-primary) 0%, var(--an-secondary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-description {
  font-size: 1.1rem;
  color: var(--an-text-secondary);
  line-height: 1.6;
  margin-bottom: 32px;
}

.tools-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.tool-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--an-white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 78, 80, 0.08);
  transition: all 0.2s ease;
}

.tool-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 78, 80, 0.12);
}

.tool-item.future-tool {
  opacity: 0.7;
  border: 1px dashed var(--an-gray3);
  background: var(--an-background-light);
}

.tool-icon {
  font-size: 1.5rem;
}

.tool-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--an-text-primary);
}

.future-note {
  font-size: 12px;
  color: var(--an-text-secondary);
  font-style: italic;
  margin: 0;
}

.calculators-action {
  background: var(--an-white);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 78, 80, 0.1);
  border: 2px solid var(--an-primary);
}

.action-content {
  padding: 40px 32px;
  text-align: center;
}

.action-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 12px;
}

.action-content p {
  color: var(--an-text-secondary);
  margin-bottom: 24px;
  font-size: 14px;
}

.explore-button {
  margin-bottom: 20px;
  width: 100%;
}

.action-features {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: var(--an-text-secondary);
}

.action-features span {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .financial-calculators-section {
    padding: 40px 0;
  }

  .section-container {
    padding: 0 16px;
  }

  .calculators-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .section-description {
    font-size: 1rem;
  }

  .tools-list {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .action-content {
    padding: 32px 24px;
  }

  .action-content h3 {
    font-size: 1.3rem;
  }
}

@media (max-width: 480px) {
  .tools-list {
    grid-template-columns: 1fr;
  }

  .tool-item {
    padding: 10px;
  }

  .action-content {
    padding: 24px 16px;
  }
}
</style>
